/* eslint-disable react/jsx-props-no-spreading */
import { SpeedDialProps } from '@mui/lab';
import {
  Backdrop, BackdropProps, FabProps, SpeedDial, SpeedDialAction, SpeedDialActionProps, SpeedDialIcon, SxProps, Theme, useTheme
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { ConfirmDialogButton, OkCancelConfirmDialog, YesNoConfirmDialog } from '../ConfirmDialog';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const speedDialOpenFabSx = (theme: Theme) => ({
  height: theme.spacing(5),
  width: theme.spacing(5),
  borderRadius: '50%',
  zIndex: theme.zIndex.drawer + 2,
});

const speedDialCloseFabSx = (theme: Theme) => ({
  height: theme.spacing(7),
  width: theme.spacing(7),
  borderRadius: theme.spacing(2),
  zIndex: theme.zIndex.drawer + 2,
});

const speedDialActionSx = (theme: Theme) => ({
  '& .MuiSpeedDialAction-fab': {
    height: theme.spacing(7),
    width: theme.spacing(7),
    borderRadius: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 2,
  },
  '& .MuiSpeedDialAction-staticTooltipLabel': {
    width: 'max-content',
    backgroundColor: 'transparent !important',
    color: theme.palette.common.white,
    boxShadow: 'none',
    fontWeight: 'bold',
    fontSize: theme.typography.body1.fontSize,
    zIndex: theme.zIndex.drawer + 2,
  },
});

export type CustomConfirmDialogProps = {
  type: 'custom';
  leftButton?: ConfirmDialogButton;
  rightButton?: ConfirmDialogButton;
  title: string;
  content: string | React.ReactNode;
  data?: object;
};

export type OkCancelConfirmDialogProps = {
  type: 'okCancel';
  title: string;
  content: string | React.ReactNode;
  data?: object;
};

export type YesNoConfirmDialogProps = {
  type: 'yesNo';
  title: string;
  content: string | React.ReactNode;
  data?: object;
  onNo: (
    // eslint-disable-next-line no-unused-vars
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined
  ) => void;
};

export interface ActionItemType {
  icon: React.ReactChild;
  title: string;
  onclick: (
    // eslint-disable-next-line no-unused-vars
    e: React.SyntheticEvent | undefined,
  ) => void;
  disabled?: boolean;
  confirm?: CustomConfirmDialogProps | YesNoConfirmDialogProps | OkCancelConfirmDialogProps;
}

interface FloatingActionButtonProps {
  actionList: ActionItemType[];
  ariaLabel?: string;
  backdropProps?: Omit<BackdropProps, 'open'>;
  closeIcon: React.ReactNode;
  fabProps?: FabProps;
  openIcon: React.ReactNode;
  speedDialActionProps?: Omit<
    SpeedDialActionProps,
    'icon' | 'tooltipTitle' | 'tooltipOpen'
  >;
  customSpeedDialCloseSx?: SxProps<Theme>;
  customSpeedDialOpenSx?: SxProps<Theme>;
  speedDialProps?: Omit<
    SpeedDialProps,
    'icon' | 'ariaLabel' | 'open' | 'onOpen' | 'onClose' | 'FabProps'
  >;
  tooltipOpen?: boolean;
  // eslint-disable-next-line semi
}

const FloatingActionButton = (props: FloatingActionButtonProps) => {
  const {
    speedDialProps,
    openIcon,
    closeIcon,
    actionList,
    ariaLabel,
    fabProps,
    speedDialActionProps,
    tooltipOpen,
    customSpeedDialCloseSx,
    customSpeedDialOpenSx,
    backdropProps,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();

  const _onDialogLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined, action: ActionItemType) => {
      setDialogOpen(false);
      if (action.confirm && action.confirm.type === 'yesNo') {
        action.confirm.onNo(e);
      } else if (action.confirm && action.confirm.type === 'custom') {
        action.confirm.leftButton?.onClick(e, undefined);
      }
    },
    [],
  );

  const _onDialogRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined, action: ActionItemType) => {
      if (action.onclick) {
        action.onclick(e);
        setDialogOpen(false);
        setIsOpen(false);
      }
    },
    [],
  );

  // eslint-disable-next-line consistent-return
  const _renderConfirm = useCallback((action: ActionItemType) => {
    const confirmKey = `${action?.confirm?.title} ${action?.confirm?.content}`;
    if (action.confirm) {
      switch (action.confirm.type) {
        case 'yesNo':
          return (
            <YesNoConfirmDialog
              key={confirmKey}
              open={dialogOpen}
              title={action.confirm.title}
              content={action.confirm.content}
              data={action.confirm.data}
              onYes={(e) => _onDialogRight(e, action)}
              onNo={(e) => _onDialogLeft(e, action)}
            />
          );
        case 'okCancel':
          return (
            <OkCancelConfirmDialog
              key={confirmKey}
              open={dialogOpen}
              title={action.confirm.title}
              content={action.confirm.content}
              data={action.confirm.data}
              onOk={(e) => _onDialogRight(e, action)}
              onCancel={() => _onDialogLeft(undefined, action)}
            />
          );
        case 'custom':
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <ConfirmDialog key={confirmKey} {...action.confirm} open={dialogOpen} />;
        default:
          // eslint-disable-next-line react/jsx-no-useless-fragment
          return undefined;
      }
    }
  }, [dialogOpen]);

  const _onActionClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined, action: ActionItemType) => {
      if (action.onclick) {
        if (action.confirm) {
          setDialogOpen(true);
        } else {
          action.onclick(e);
        }
      }
    },
    [setDialogOpen],
  );

  return (
    <>
      <SpeedDial
        {...speedDialProps}
        data-testid="speed-dial"
        ariaLabel={ariaLabel || 'Floating Action Button'}
        icon={<SpeedDialIcon icon={openIcon} openIcon={closeIcon} />}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        open={isOpen}
        FabProps={{
          ...fabProps,
          sx: [
            isOpen && speedDialOpenFabSx(theme),
            !isOpen && speedDialCloseFabSx(theme),
            ...((isOpen && (Array.isArray(customSpeedDialOpenSx))) ? customSpeedDialOpenSx : [customSpeedDialOpenSx]),
            ...((!isOpen && (Array.isArray(customSpeedDialCloseSx))) ? customSpeedDialCloseSx : [customSpeedDialCloseSx]),
          ],
        }}
      >
        {(actionList.length > 6 ? actionList.slice(0, 6) : actionList).filter((item) => !item.disabled).map(
          (action) => (
            <SpeedDialAction
              {...speedDialActionProps}
              aria-label="actions"
              key={action.title}
              icon={action.icon}
              tooltipTitle={action.title}
              tooltipOpen={tooltipOpen || true}
              onClick={(e) => _onActionClick(e, action)}
              sx={speedDialActionSx(theme)}
            />
          ),
        )}
      </SpeedDial>
      {(actionList.filter((item) => !item.disabled)).map(
        (action) => (
          action.confirm && _renderConfirm(action)
        )
      )}
      <Backdrop {...backdropProps} open={isOpen} />
    </>
  );
};

export default FloatingActionButton;
